import type React from "react";
import { useEffect } from "react";
import { Link } from "react-router";
import { Helmet } from "react-helmet-async";
import { APP_ROUTES } from "@/pages/routes";

const AboutPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="flex min-h-screen w-full flex-col bg-white dark:bg-gray-950">
      <Helmet>
        <title>About MycenAI — AI Support for Psychedelic-Assisted Therapy</title>
        <meta name="description" content="MycenAI provides AI-powered clinical documentation tools for psychedelic-assisted therapy practitioners. Built by a psychologist, powered by ConfideAI's confidential computing platform." />
        <link rel="canonical" href="https://mycenai.org/about/" />
        <meta property="og:title" content="About MycenAI — AI Support for Psychedelic-Assisted Therapy" />
        <meta property="og:description" content="MycenAI provides AI-powered clinical documentation tools for psychedelic-assisted therapy practitioners." />
        <meta property="og:url" content="https://mycenai.org/about/" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Header */}
      <header className="border-gray-200 border-b bg-white/80 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/80">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to={APP_ROUTES.SPLASH}>
            <span className="font-bold text-gray-900 text-xl dark:text-white">
              Mycen<span className="text-violet-600 dark:text-violet-400">AI</span>
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to={APP_ROUTES.BLOG} className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400">Blog</Link>
            <Link to={APP_ROUTES.RESOURCES} className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400">Resources</Link>
            <Link to={APP_ROUTES.ABOUT} className="hidden font-medium text-gray-700 text-sm transition hover:text-violet-600 sm:inline dark:text-gray-300 dark:hover:text-violet-400">About</Link>
            <a href="https://confideai.ai/welcome/mycenai" className="rounded-lg bg-violet-600 px-6 py-2.5 font-semibold text-sm text-white shadow-sm transition hover:bg-violet-700">Get Started</a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6">
          <Link to={APP_ROUTES.SPLASH} className="mb-8 inline-block text-gray-500 text-sm transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">← Home</Link>

          <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-bold prose-a:text-violet-600 dark:prose-a:text-violet-400">
            <h1>About MycenAI</h1>

            <h2>The PAT Community's AI Platform</h2>
            <p>
              MycenAI is the psychedelic-assisted therapy (PAT) community's dedicated gateway to <a href="https://confideai.ai">ConfideAI</a> — a confidential AI platform built by a psychologist for mental health professionals. While ConfideAI serves the broader mental health field, MycenAI focuses specifically on the unique needs of PAT practitioners.
            </p>
            <p>
              Psychedelic-assisted therapy is at a critical inflection point. As research expands and regulatory frameworks evolve, practitioners need tools that match the sensitivity and complexity of this work. Documentation in PAT isn't just administrative — it's part of the clinical and ethical framework that makes this modality safe and effective.
            </p>

            <h2>Why PAT Needs Its Own Tools</h2>
            <p>
              Psychedelic-assisted therapy involves preparation, ceremony or dosing sessions, and integration — each with distinct documentation requirements. Standard clinical documentation tools don't account for the non-ordinary states of consciousness, the somatic experiences, or the integration processes that are central to this work.
            </p>
            <p>
              PAT practitioners also face heightened confidentiality concerns. Clients undergoing psychedelic therapy may be participating in clinical trials, using substances that remain legally complex, or exploring deeply vulnerable psychological territory. The privacy of these interactions is paramount.
            </p>

            <h2>What MycenAI Provides</h2>
            <p>
              Through ConfideAI's platform, MycenAI gives PAT practitioners access to:
            </p>
            <ul>
              <li><strong>Clinical templates</strong> designed for preparation, ceremony, and integration documentation</li>
              <li><strong>Orientation-aware prompts</strong> that adapt to somatic, transpersonal, IFS, psychodynamic, and other modalities commonly used in PAT</li>
              <li><strong>Document chaining</strong> — output from one session template feeds into the next, building a coherent clinical record across an entire course of treatment</li>
              <li><strong>Research-backed resources</strong> grounding every template in peer-reviewed clinical literature</li>
            </ul>

            <h2>Privacy That Matches the Sensitivity</h2>
            <p>
              All AI processing happens inside hardware-secured Trusted Execution Environments (TEE). No clinical content is stored on any server. No data is used for AI training. Cryptographic attestation proves your data was processed in a genuine secure enclave.
            </p>
            <p>
              For PAT practitioners, this isn't a nice-to-have — it's essential. The confidentiality of psychedelic therapy sessions requires the strongest possible privacy guarantees, and hardware-enforced isolation provides exactly that.
            </p>

            <h2>Our Mission</h2>
            <p>
              MycenAI exists to support the growing PAT community with tools that respect the depth, sensitivity, and clinical rigour this work demands. We believe AI can meaningfully reduce the documentation burden on practitioners — freeing them to focus on what matters most: their clients' healing journeys.
            </p>

            <hr />
            <p>
              <strong>Ready to get started?</strong> Access the platform at <a href="https://confideai.ai/welcome/mycenai">confideai.ai</a>, or reach out at <a href="mailto:support@confideai.ai">support@confideai.ai</a>.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-gray-200 border-t bg-gray-50 px-6 py-8 dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center">
          <p className="text-gray-500 text-sm dark:text-gray-400">© 2026 MycenAI. All rights reserved.</p>
          <div className="flex gap-6 text-gray-500 text-sm dark:text-gray-400">
            <Link to={APP_ROUTES.FAQ} className="transition hover:text-violet-600 dark:hover:text-violet-400">FAQ</Link>
            <Link to={APP_ROUTES.PRIVACY} className="transition hover:text-violet-600 dark:hover:text-violet-400">Privacy</Link>
            <Link to={APP_ROUTES.TERMS} className="transition hover:text-violet-600 dark:hover:text-violet-400">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
